/**
 * Partner Portal - /partners
 * 
 * Features:
 * - Partner login (email + referral code)
 * - Commission tracking and earnings
 * - Referral link management
 * - Enterprise visibility (their referrals)
 * - Payout history
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Handshake,
  DollarSign,
  Users,
  Link2,
  Copy,
  Check,
  LogOut,
  Building2,
  TrendingUp,
  Calendar,
  Download,
  Eye,
  EyeOff,
  ChevronRight,
  Loader2,
  AlertCircle,
  CreditCard,
  Receipt,
  ChevronLeft,
  HelpCircle,
  BookOpen,
  Zap,
  FileText,
  Award,
  Banknote,
  Plus,
  Mail,
  Phone,
  Globe,
  Clock,
  CheckCircle,
  XCircle,
  Key,
  Send,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import DocumentsSection from "@/components/DocumentsSection";

// Partner Guide Steps
const partnerGuideSteps = [
  {
    title: "1. Share Your Referral Link",
    icon: Link2,
    description: "Your unique referral link tracks all enterprise sign-ups. Share it with potential enterprise clients.",
    tips: ["Copy link from dashboard", "Use in emails and marketing", "Track conversions in real-time"]
  },
  {
    title: "2. Enterprise Signs Up",
    icon: Building2,
    description: "When an enterprise registers using your link, they're automatically linked to your partner account.",
    tips: ["Enterprise chooses their plan (Builder/Scaler/Infinite)", "They get wholesale pricing", "You earn commission on every purchase"]
  },
  {
    title: "3. Earn Recurring Commissions",
    icon: DollarSign,
    description: "Earn commission on all enterprise purchases - initial and renewals! Start at 10% and grow to 20% as you bring more clients.",
    tips: ["Commissions based on your tier level", "Higher tiers = higher commission rates", "Tier evaluation at each anniversary"]
  },
  {
    title: "4. Get Paid",
    icon: CreditCard,
    description: "Request payouts once you reach the minimum threshold. We'll process your payment via PayPal.",
    tips: ["Minimum threshold set by admin", "Request payout from dashboard", "Receive payment to your PayPal"]
  },
  {
    title: "5. Monitor & Grow",
    icon: TrendingUp,
    description: "Track your enterprise clients' status, plans, and your total earnings. Grow your tier to earn more!",
    tips: ["See all active enterprises", "Track your tier progress", "View payout history"]
  }
];

export default function PartnerPortal() {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [partner, setPartner] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [showGuide, setShowGuide] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Tier & Payout state
  const [tierStatus, setTierStatus] = useState(null);
  const [payoutHistory, setPayoutHistory] = useState(null);
  const [requestingPayout, setRequestingPayout] = useState(false);
  const [paypalEmail, setPaypalEmail] = useState("");
  
  // Transactions
  const [transactions, setTransactions] = useState([]);
  const [transactionSummary, setTransactionSummary] = useState(null);
  const [transactionPage, setTransactionPage] = useState(1);
  const [transactionPages, setTransactionPages] = useState(1);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  
  // Login form
  const [email, setEmail] = useState("");
  const [referralCode, setReferralCode] = useState("");
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  
  // Add Enterprise state (Phase 3)
  const [showAddEnterpriseModal, setShowAddEnterpriseModal] = useState(false);
  const [addEnterpriseLoading, setAddEnterpriseLoading] = useState(false);
  const [newEnterpriseData, setNewEnterpriseData] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    country: "Canada",
    notes: ""
  });
  const [selectedEnterpriseDetail, setSelectedEnterpriseDetail] = useState(null);
  const [productionRequestLoading, setProductionRequestLoading] = useState(false);
  
  // UI state
  const [showReferralCode, setShowReferralCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Check for existing session
  useEffect(() => {
    const token = localStorage.getItem("partnerToken");
    const partnerId = localStorage.getItem("partnerId");
    if (token && partnerId) {
      fetchPartnerData(partnerId, token);
    } else {
      setLoading(false);
    }
  }, []);
  
  // Fetch transactions when tab changes
  useEffect(() => {
    if (isLoggedIn && activeTab === 'transactions') {
      fetchTransactions();
    }
  }, [activeTab, transactionPage, isLoggedIn]);
  
  const fetchTransactions = async () => {
    const partnerId = localStorage.getItem("partnerId");
    const token = localStorage.getItem("partnerToken");
    if (!partnerId || !token) return;
    
    setLoadingTransactions(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await apiClient.get(`/b2b/partners/${partnerId}/transactions?page=${transactionPage}`, { headers });
      setTransactions(res.data.transactions || []);
      setTransactionSummary(res.data.summary || null);
      setTransactionPages(res.data.pages || 1);
    } catch (err) {
      console.error("Failed to fetch transactions:", err);
    } finally {
      setLoadingTransactions(false);
    }
  };
  
  const fetchPartnerData = async (partnerId, token) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Get partner details
      const partnerRes = await apiClient.get(`/b2b/partners/${partnerId}`, { headers });
      setPartner(partnerRes.data);
      
      // Get earnings
      const earningsRes = await apiClient.get(`/b2b/partners/${partnerId}/earnings`, { headers });
      setEarnings(earningsRes.data);
      
      // Get tier status (includes commission rate, next tier requirements)
      try {
        const tierRes = await apiClient.get(`/b2b/partners/${partnerId}/tier-status`, { headers });
        setTierStatus(tierRes.data);
      } catch { setTierStatus(null); }
      
      // Get payout history
      try {
        const payoutRes = await apiClient.get(`/b2b/partners/${partnerId}/payouts`, { headers });
        setPayoutHistory(payoutRes.data);
      } catch { setPayoutHistory(null); }
      
      setIsLoggedIn(true);
    } catch (error) {
      console.error("Error fetching partner data:", error);
      localStorage.removeItem("partnerToken");
      localStorage.removeItem("partnerId");
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !referralCode) {
      toast.error("Please enter email and referral code");
      return;
    }
    
    setLoginLoading(true);
    try {
      // Verify partner credentials
      const res = await apiClient.post("/b2b/partners/login", { email, referral_code: referralCode });
      
      if (res.data.partner_id) {
        localStorage.setItem("partnerToken", res.data.token || "partner-session");
        localStorage.setItem("partnerId", res.data.partner_id);
        await fetchPartnerData(res.data.partner_id, res.data.token || "partner-session");
        toast.success("Welcome back!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoginLoading(false);
    }
  };
  
  const handleLogout = () => {
    localStorage.removeItem("partnerToken");
    localStorage.removeItem("partnerId");
    setIsLoggedIn(false);
    setPartner(null);
    setEarnings(null);
  };
  
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address first");
      return;
    }
    setForgotPasswordLoading(true);
    try {
      await apiClient.post("/b2b/forgot-password", {
        email: email,
        portal_type: "partner"
      });
      setForgotPasswordSent(true);
      toast.success("If a partner account exists, a reset link has been sent");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reset email");
    } finally {
      setForgotPasswordLoading(false);
    }
  };
  
  const copyReferralLink = () => {
    const link = `${window.location.origin}/auth?ref=${partner?.referral_code}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(true);
    toast.success("Referral link copied!");
    setTimeout(() => setCopiedLink(false), 2000);
  };
  
  // Phase 3: Add Enterprise function
  const handleAddEnterprise = async (e) => {
    e.preventDefault();
    if (!newEnterpriseData.company_name || !newEnterpriseData.email || !newEnterpriseData.contact_name) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setAddEnterpriseLoading(true);
    try {
      const token = localStorage.getItem("partnerToken");
      const res = await apiClient.post("/b2b/partners/create-enterprise", {
        ...newEnterpriseData,
        partner_id: partner?.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Enterprise created! Documents have been sent automatically.");
      setShowAddEnterpriseModal(false);
      setNewEnterpriseData({
        company_name: "",
        contact_name: "",
        email: "",
        phone: "",
        country: "Canada",
        notes: ""
      });
      
      // Refresh partner data to show new enterprise
      await fetchPartnerData(partner?.id, token);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create enterprise");
    } finally {
      setAddEnterpriseLoading(false);
    }
  };
  
  // Phase 3: Request Production API for an Enterprise
  const handleRequestProduction = async (enterpriseId) => {
    setProductionRequestLoading(true);
    try {
      const token = localStorage.getItem("partnerToken");
      await apiClient.post(`/b2b/partners/enterprises/${enterpriseId}/request-production`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Production access request submitted. Admin will review.");
      setSelectedEnterpriseDetail(null);
      
      // Refresh partner data
      await fetchPartnerData(partner?.id, token);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request production access");
    } finally {
      setProductionRequestLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-viraly-dark flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-viraly-orange animate-spin" />
      </div>
    );
  }
  
  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-viraly-dark flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/20 to-blue-600/20 flex items-center justify-center mx-auto mb-4">
              <Handshake className="w-8 h-8 text-blue-400" />
            </div>
            <h1 className="text-2xl font-bold text-viraly-light">Partner Portal</h1>
            <p className="text-viraly-muted mt-2">Access your commission dashboard</p>
          </div>
          
          <form onSubmit={handleLogin} className="card-base p-6 space-y-4">
            <div>
              <label className="text-sm text-viraly-muted block mb-1">Email Address</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="partner@company.com"
                className="bg-white/5 border-white/10"
                data-testid="partner-email"
              />
            </div>
            
            <div>
              <label className="text-sm text-viraly-muted block mb-1">Referral Code</label>
              <Input
                type="text"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                placeholder="P-XXXXXXXX"
                className="bg-white/5 border-white/10 uppercase"
                data-testid="partner-code"
              />
            </div>
            
            <Button
              type="submit"
              disabled={loginLoading}
              className="w-full btn-primary"
              data-testid="partner-login-btn"
            >
              {loginLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Sign In
            </Button>
            
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="w-full text-sm text-viraly-muted hover:text-blue-400 transition-colors"
              data-testid="partner-forgot-password-link"
            >
              Forgot your password?
            </button>
            
            <p className="text-center text-xs text-viraly-muted">
              Not a partner yet? <a href="/#enterprise" className="text-viraly-orange hover:underline">Apply here</a>
            </p>
          </form>
          
          {/* Forgot Password Modal */}
          <AnimatePresence>
            {showForgotPassword && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
                onClick={() => setShowForgotPassword(false)}
              >
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.95, opacity: 0 }}
                  onClick={(e) => e.stopPropagation()}
                  className="bg-viraly-card border border-white/10 rounded-2xl p-6 w-full max-w-md mx-4"
                >
                  {!forgotPasswordSent ? (
                    <>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-bold text-viraly-light">Reset Password</h3>
                        <button
                          onClick={() => setShowForgotPassword(false)}
                          className="text-viraly-muted hover:text-white"
                        >
                          <AlertCircle className="w-5 h-5" />
                        </button>
                      </div>
                      
                      <p className="text-viraly-muted text-sm mb-4">
                        Enter your partner email address and we'll send you a link to reset your password.
                      </p>
                      
                      <form onSubmit={handleForgotPassword}>
                        <div className="mb-4">
                          <label className="text-sm text-viraly-light mb-2 block">Partner Email</label>
                          <Input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="partner@company.com"
                            className="bg-white/5 border-white/10"
                            required
                            data-testid="partner-forgot-email-input"
                          />
                        </div>
                        
                        <div className="flex gap-3">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowForgotPassword(false)}
                            className="flex-1"
                          >
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            disabled={forgotPasswordLoading}
                            className="flex-1 btn-primary"
                            data-testid="partner-forgot-submit-btn"
                          >
                            {forgotPasswordLoading ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              "Send Reset Link"
                            )}
                          </Button>
                        </div>
                      </form>
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                        <Check className="w-8 h-8 text-green-400" />
                      </div>
                      <h3 className="text-xl font-bold text-viraly-light mb-2">Check Your Email</h3>
                      <p className="text-viraly-muted text-sm mb-4">
                        If a partner account exists with this email, you will receive a password reset link shortly.
                      </p>
                      <Button
                        onClick={() => {
                          setShowForgotPassword(false);
                          setForgotPasswordSent(false);
                        }}
                        className="btn-primary"
                      >
                        Back to Login
                      </Button>
                    </div>
                  )}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    );
  }
  
  // Dashboard
  return (
    <div className="min-h-screen bg-viraly-dark">
      {/* Header */}
      <header className="border-b border-white/10 bg-viraly-dark/80 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <Handshake className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h1 className="font-bold text-viraly-light">{partner?.company_name}</h1>
              <p className="text-xs text-viraly-muted">Partner Portal</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowGuide(true)}
              className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              How to Use
            </Button>
            <a 
              href="/downloads/Viraly_Partner_Onboarding_Guide.docx" 
              download
              className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-white/10 text-viraly-muted hover:text-viraly-light hover:bg-white/5 transition-colors"
            >
              <Download className="w-4 h-4" />
              Guide
            </a>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>
      
      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-white/10 pb-4">
          {[
            { id: 'overview', label: 'Overview', icon: TrendingUp },
            { id: 'performance', label: 'Performance', icon: Award },
            { id: 'transactions', label: 'Transactions', icon: Receipt },
            { id: 'resources', label: 'Resources', icon: FileText },
            { id: 'enterprises', label: 'Enterprises', icon: Building2 },
            { id: 'documents', label: 'Documents', icon: FileText }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                activeTab === tab.id 
                  ? 'bg-blue-500/20 text-blue-400' 
                  : 'text-viraly-muted hover:text-viraly-light hover:bg-white/5'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
        
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-4 gap-4 mb-8">
          <div className="card-base p-6 text-center">
            <DollarSign className="w-8 h-8 text-green-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-green-400">
              ${(earnings?.total_commission_cad || 0).toFixed(2)}
            </p>
            <p className="text-viraly-muted text-sm">Total Earnings</p>
          </div>
          
          <div className="card-base p-6 text-center">
            <Building2 className="w-8 h-8 text-purple-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-viraly-light">
              {partner?.total_referrals || 0}
            </p>
            <p className="text-viraly-muted text-sm">Total Referrals</p>
          </div>
          
          <div className="card-base p-6 text-center">
            <TrendingUp className="w-8 h-8 text-blue-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-viraly-light">
              {tierStatus?.tier_name || `${((partner?.commission_rate || 0.1) * 100).toFixed(1)}%`}
            </p>
            <p className="text-viraly-muted text-sm">
              {tierStatus ? `${(tierStatus.commission_rate * 100).toFixed(1)}% Commission` : 'Commission Rate'}
            </p>
          </div>
          
          <div className="card-base p-6 text-center">
            <DollarSign className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-yellow-400">
              ${(payoutHistory?.pending_balance_cad || earnings?.pending_payout_cad || 0).toFixed(2)}
            </p>
            <p className="text-viraly-muted text-sm">Pending Payout</p>
            {payoutHistory?.can_request_payout && (
              <Button 
                size="sm" 
                className="mt-2 bg-green-500 hover:bg-green-600"
                onClick={() => setRequestingPayout(true)}
              >
                Request Payout
              </Button>
            )}
          </div>
        </div>
        
        {/* Tier Progress Card */}
        {tierStatus && tierStatus.next_tier && (
          <div className="card-base p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-purple-400" />
                <h3 className="font-bold text-viraly-light">Tier Progress</h3>
              </div>
              <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-1 rounded">
                Year {tierStatus.years_as_partner + 1} • Ends {new Date(tierStatus.period_end).toLocaleDateString()}
              </span>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-white/5 rounded-lg p-4">
                <p className="text-xs text-viraly-muted mb-1">Active Enterprises</p>
                <p className="text-xl font-bold text-viraly-light">{tierStatus.active_enterprises}</p>
                <p className="text-xs text-viraly-muted">Need {tierStatus.next_tier.enterprises_needed} more for {tierStatus.next_tier.name}</p>
              </div>
              <div className="bg-white/5 rounded-lg p-4">
                <p className="text-xs text-viraly-muted mb-1">Revenue This Period</p>
                <p className="text-xl font-bold text-viraly-light">${tierStatus.revenue_current_period_cad?.toLocaleString()}</p>
                <p className="text-xs text-viraly-muted">Need ${tierStatus.next_tier.revenue_needed_cad?.toLocaleString()} more</p>
              </div>
            </div>
            
            <div className="text-sm text-viraly-muted">
              <p>Next tier: <span className="text-purple-400 font-semibold">{tierStatus.next_tier.name}</span> ({(tierStatus.next_tier.commission_rate * 100).toFixed(1)}% commission)</p>
            </div>
          </div>
        )}
        
        {/* Referral Link Card */}
        <div className="card-base p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-viraly-orange" />
              <h3 className="font-bold text-viraly-light">Your Referral Link</h3>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-viraly-muted">Code:</span>
              <button
                onClick={() => setShowReferralCode(!showReferralCode)}
                className="flex items-center gap-1 text-sm font-mono bg-white/10 px-3 py-1 rounded"
              >
                {showReferralCode ? partner?.referral_code : "P-••••••••"}
                {showReferralCode ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              </button>
            </div>
          </div>
          
          <div className="flex gap-3">
            <div className="flex-1 bg-white/5 rounded-lg p-3 font-mono text-sm text-viraly-light truncate">
              {window.location.origin}/auth?ref={partner?.referral_code}
            </div>
            <Button onClick={copyReferralLink} className="btn-primary">
              {copiedLink ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
          
          <p className="text-xs text-viraly-muted mt-3">
            Share this link with potential enterprise clients. You'll earn {((partner?.commission_rate || 0.2) * 100).toFixed(0)}% lifetime commission on all their payments.
          </p>
        </div>
          </>
        )}
        
        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <div>
            {/* Transaction Summary */}
            {transactionSummary && (
              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="card-base p-6">
                  <p className="text-sm text-viraly-muted">Total Revenue Generated</p>
                  <p className="text-2xl font-bold text-viraly-light">${transactionSummary.total_revenue_cad?.toFixed(2) || '0.00'}</p>
                </div>
                <div className="card-base p-6">
                  <p className="text-sm text-viraly-muted">Your Total Commission</p>
                  <p className="text-2xl font-bold text-green-400">${transactionSummary.total_commission_cad?.toFixed(2) || '0.00'}</p>
                </div>
                <div className="card-base p-6">
                  <p className="text-sm text-viraly-muted">Pending Payout</p>
                  <p className="text-2xl font-bold text-yellow-400">${transactionSummary.pending_payout_cad?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            )}
            
            {/* Transaction List */}
            <div className="card-base overflow-hidden">
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <h3 className="font-bold text-viraly-light">Transaction History</h3>
                <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">
                  {transactions.length} transactions
                </span>
              </div>
              
              {loadingTransactions ? (
                <div className="p-8 text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-viraly-muted mx-auto" />
                </div>
              ) : transactions.length > 0 ? (
                <>
                  <table className="w-full">
                    <thead className="bg-white/5">
                      <tr className="text-left text-viraly-muted text-sm">
                        <th className="p-4">Date</th>
                        <th className="p-4">Enterprise</th>
                        <th className="p-4">Description</th>
                        <th className="p-4">Payment</th>
                        <th className="p-4">Your Commission</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map(t => (
                        <tr key={t.id} className="border-t border-white/5 hover:bg-white/5">
                          <td className="p-4 text-sm text-viraly-muted">
                            {new Date(t.created_at).toLocaleDateString()}
                          </td>
                          <td className="p-4 font-medium text-viraly-light">{t.enterprise_name}</td>
                          <td className="p-4 text-sm text-viraly-muted">{t.description}</td>
                          <td className="p-4 text-viraly-light">${t.amount_cad?.toFixed(2)}</td>
                          <td className="p-4">
                            <span className="text-green-400 font-medium">${t.commission_cad?.toFixed(2)}</span>
                            <span className="text-xs text-viraly-muted ml-2">({(t.commission_rate * 100).toFixed(0)}%)</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {/* Pagination */}
                  {transactionPages > 1 && (
                    <div className="p-4 border-t border-white/10 flex items-center justify-center gap-4">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        disabled={transactionPage === 1}
                        onClick={() => setTransactionPage(p => p - 1)}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm text-viraly-muted">
                        Page {transactionPage} of {transactionPages}
                      </span>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        disabled={transactionPage === transactionPages}
                        onClick={() => setTransactionPage(p => p + 1)}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <div className="p-8 text-center">
                  <Receipt className="w-12 h-12 text-viraly-muted mx-auto mb-4" />
                  <p className="text-viraly-muted">No transactions yet</p>
                  <p className="text-xs text-viraly-muted mt-2">
                    When your referred enterprises make payments, you'll see them here
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Enterprises Tab - Phase 3: Enhanced with Add Enterprise */}
        {activeTab === 'enterprises' && (
          <div className="space-y-6">
            {/* Header with Add Enterprise Button */}
            <div className="card-base p-4 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-viraly-light">Your Enterprises</h3>
                <p className="text-xs text-viraly-muted">Manage enterprises you've onboarded</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded">
                  {partner?.enterprise_count || 0} total
                </span>
                <Button 
                  className="bg-viraly-orange hover:bg-viraly-orange/90"
                  onClick={() => setShowAddEnterpriseModal(true)}
                  data-testid="partner-add-enterprise-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Enterprise
                </Button>
              </div>
            </div>
            
            {/* Enterprises List */}
            {partner?.enterprises && partner.enterprises.length > 0 ? (
              <div className="card-base overflow-hidden">
                <table className="w-full">
                  <thead className="bg-white/5">
                    <tr className="text-left text-viraly-muted text-sm">
                      <th className="p-4">Company</th>
                      <th className="p-4">Onboarding</th>
                      <th className="p-4">Sandbox Key</th>
                      <th className="p-4">Production</th>
                      <th className="p-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partner.enterprises.map(enterprise => {
                      const statusColors = {
                        'pending_documents': 'bg-gray-500/20 text-gray-400',
                        'documents_sent': 'bg-blue-500/20 text-blue-400',
                        'documents_signed': 'bg-purple-500/20 text-purple-400',
                        'sandbox_testing': 'bg-yellow-500/20 text-yellow-400',
                        'production_ready': 'bg-orange-500/20 text-orange-400',
                        'active': 'bg-green-500/20 text-green-400'
                      };
                      const statusColor = statusColors[enterprise.onboarding_status] || statusColors.pending_documents;
                      
                      return (
                        <tr key={enterprise.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`partner-enterprise-row-${enterprise.id}`}>
                          <td className="p-4">
                            <p className="font-medium text-viraly-light">{enterprise.company_name}</p>
                            <p className="text-xs text-viraly-muted">{enterprise.email}</p>
                          </td>
                          <td className="p-4">
                            <span className={`px-2 py-1 rounded text-xs ${statusColor}`}>
                              {(enterprise.onboarding_status || 'pending_documents').replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="p-4">
                            {enterprise.sandbox_api_key_active ? (
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-mono text-viraly-muted truncate max-w-[120px]">
                                  {enterprise.sandbox_api_key?.substring(0, 20)}...
                                </span>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(enterprise.sandbox_api_key);
                                    toast.success("Copied!");
                                  }}
                                  className="text-viraly-orange hover:text-viraly-orange/80"
                                >
                                  <Copy className="w-3 h-3" />
                                </button>
                              </div>
                            ) : (
                              <span className="text-xs text-viraly-muted">Not yet active</span>
                            )}
                          </td>
                          <td className="p-4">
                            {enterprise.production_api_key_active ? (
                              <span className="flex items-center gap-1 text-xs text-green-400">
                                <CheckCircle className="w-3 h-3" />
                                Active
                              </span>
                            ) : enterprise.production_requested ? (
                              <span className="flex items-center gap-1 text-xs text-yellow-400">
                                <Clock className="w-3 h-3" />
                                Pending Approval
                              </span>
                            ) : (
                              <span className="text-xs text-viraly-muted">Not requested</span>
                            )}
                          </td>
                          <td className="p-4">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setSelectedEnterpriseDetail(enterprise)}
                              data-testid={`partner-view-enterprise-${enterprise.id}`}
                            >
                              View Details
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="card-base p-8 text-center">
                <Building2 className="w-12 h-12 text-viraly-muted mx-auto mb-4" />
                <p className="text-viraly-muted">No enterprises yet</p>
                <p className="text-xs text-viraly-muted mt-2">Add your first enterprise client or share your referral link</p>
                <Button 
                  className="mt-4 bg-viraly-orange hover:bg-viraly-orange/90"
                  onClick={() => setShowAddEnterpriseModal(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Enterprise
                </Button>
              </div>
            )}
            
            {/* Add Enterprise Modal */}
            <AnimatePresence>
              {showAddEnterpriseModal && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
                  onClick={() => setShowAddEnterpriseModal(false)}
                >
                  <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()}
                    className="bg-viraly-card border border-white/10 rounded-2xl p-6 w-full max-w-lg mx-4"
                    data-testid="add-enterprise-modal"
                  >
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="text-xl font-bold text-viraly-light">Add New Enterprise</h3>
                        <p className="text-sm text-viraly-muted">Create a new enterprise client under your partnership</p>
                      </div>
                      <button
                        onClick={() => setShowAddEnterpriseModal(false)}
                        className="text-viraly-muted hover:text-white p-2"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    
                    <form onSubmit={handleAddEnterprise} className="space-y-4">
                      <div>
                        <label className="text-sm text-viraly-muted mb-1 block">Company Name *</label>
                        <Input
                          value={newEnterpriseData.company_name}
                          onChange={(e) => setNewEnterpriseData(prev => ({ ...prev, company_name: e.target.value }))}
                          placeholder="Acme Corporation"
                          className="bg-white/5 border-white/10"
                          required
                          data-testid="enterprise-company-name"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-viraly-muted mb-1 block">Contact Name *</label>
                          <Input
                            value={newEnterpriseData.contact_name}
                            onChange={(e) => setNewEnterpriseData(prev => ({ ...prev, contact_name: e.target.value }))}
                            placeholder="John Smith"
                            className="bg-white/5 border-white/10"
                            required
                            data-testid="enterprise-contact-name"
                          />
                        </div>
                        <div>
                          <label className="text-sm text-viraly-muted mb-1 block">Email *</label>
                          <Input
                            type="email"
                            value={newEnterpriseData.email}
                            onChange={(e) => setNewEnterpriseData(prev => ({ ...prev, email: e.target.value }))}
                            placeholder="john@acme.com"
                            className="bg-white/5 border-white/10"
                            required
                            data-testid="enterprise-email"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-viraly-muted mb-1 block">Phone</label>
                          <Input
                            value={newEnterpriseData.phone}
                            onChange={(e) => setNewEnterpriseData(prev => ({ ...prev, phone: e.target.value }))}
                            placeholder="+1 555 123 4567"
                            className="bg-white/5 border-white/10"
                            data-testid="enterprise-phone"
                          />
                        </div>
                        <div>
                          <label className="text-sm text-viraly-muted mb-1 block">Country</label>
                          <select
                            value={newEnterpriseData.country}
                            onChange={(e) => setNewEnterpriseData(prev => ({ ...prev, country: e.target.value }))}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-viraly-light"
                            data-testid="enterprise-country"
                          >
                            <option value="Canada">Canada</option>
                            <option value="United States">United States</option>
                            <option value="United Kingdom">United Kingdom</option>
                            <option value="Australia">Australia</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                      </div>
                      
                      <div>
                        <label className="text-sm text-viraly-muted mb-1 block">Notes (Optional)</label>
                        <textarea
                          value={newEnterpriseData.notes}
                          onChange={(e) => setNewEnterpriseData(prev => ({ ...prev, notes: e.target.value }))}
                          placeholder="Any additional notes about this enterprise..."
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-viraly-light resize-none h-20"
                          data-testid="enterprise-notes"
                        />
                      </div>
                      
                      <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                        <p className="text-xs text-blue-400">
                          <Mail className="w-3 h-3 inline mr-1" />
                          Upon creation, the enterprise will automatically receive:
                        </p>
                        <ul className="text-xs text-blue-300 mt-2 ml-4 space-y-1">
                          <li>• Portal login credentials via email</li>
                          <li>• Legal documents (TOS, SLA, DPA) for e-signature</li>
                          <li>• Sandbox access once documents are signed</li>
                        </ul>
                      </div>
                      
                      <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowAddEnterpriseModal(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          disabled={addEnterpriseLoading}
                          className="bg-viraly-orange hover:bg-viraly-orange/90"
                          data-testid="submit-enterprise-btn"
                        >
                          {addEnterpriseLoading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Creating...
                            </>
                          ) : (
                            <>
                              <Plus className="w-4 h-4 mr-2" />
                              Create Enterprise
                            </>
                          )}
                        </Button>
                      </div>
                    </form>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
            
            {/* Enterprise Detail Modal */}
            <AnimatePresence>
              {selectedEnterpriseDetail && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
                  onClick={() => setSelectedEnterpriseDetail(null)}
                >
                  <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()}
                    className="bg-viraly-card border border-white/10 rounded-2xl p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto"
                    data-testid="enterprise-detail-modal"
                  >
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="text-xl font-bold text-viraly-light">{selectedEnterpriseDetail.company_name}</h3>
                        <p className="text-sm text-viraly-muted">{selectedEnterpriseDetail.email}</p>
                      </div>
                      <button
                        onClick={() => setSelectedEnterpriseDetail(null)}
                        className="text-viraly-muted hover:text-white p-2"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    
                    {/* Onboarding Progress */}
                    <div className="mb-6">
                      <div className="flex justify-between mb-2">
                        <span className="text-sm text-viraly-muted">Onboarding Progress</span>
                        <span className="text-sm text-viraly-orange">
                          {(selectedEnterpriseDetail.onboarding_status || 'pending_documents').replace(/_/g, ' ')}
                        </span>
                      </div>
                      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-viraly-orange to-orange-400 transition-all duration-500"
                          style={{ 
                            width: `${
                              selectedEnterpriseDetail.onboarding_status === 'active' ? 100 :
                              selectedEnterpriseDetail.onboarding_status === 'production_ready' ? 90 :
                              selectedEnterpriseDetail.onboarding_status === 'sandbox_testing' ? 70 :
                              selectedEnterpriseDetail.onboarding_status === 'documents_signed' ? 50 :
                              selectedEnterpriseDetail.onboarding_status === 'documents_sent' ? 30 : 10
                            }%` 
                          }}
                        />
                      </div>
                    </div>
                    
                    {/* Enterprise Info */}
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="bg-white/5 rounded-lg p-3">
                        <p className="text-xs text-viraly-muted">Contact</p>
                        <p className="font-semibold text-viraly-light">{selectedEnterpriseDetail.contact_name}</p>
                        <p className="text-xs text-viraly-muted">{selectedEnterpriseDetail.phone || 'No phone'}</p>
                      </div>
                      <div className="bg-white/5 rounded-lg p-3">
                        <p className="text-xs text-viraly-muted">Country</p>
                        <p className="font-semibold text-viraly-light">{selectedEnterpriseDetail.country || 'Not specified'}</p>
                      </div>
                    </div>
                    
                    {/* API Keys Section */}
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-viraly-light mb-3 flex items-center gap-2">
                        <Key className="w-4 h-4" />
                        API Access
                      </h4>
                      <div className="space-y-3">
                        {/* Sandbox Key */}
                        <div className="bg-white/5 rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-viraly-light flex items-center gap-2">
                                Sandbox API Key
                                <span className={`text-xs px-2 py-0.5 rounded ${selectedEnterpriseDetail.sandbox_api_key_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                  {selectedEnterpriseDetail.sandbox_api_key_active ? 'Active' : 'Inactive'}
                                </span>
                              </p>
                              {selectedEnterpriseDetail.sandbox_api_key_active && (
                                <p className="text-xs text-viraly-muted font-mono mt-1">
                                  {selectedEnterpriseDetail.sandbox_api_key}
                                </p>
                              )}
                            </div>
                            {selectedEnterpriseDetail.sandbox_api_key_active && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  navigator.clipboard.writeText(selectedEnterpriseDetail.sandbox_api_key);
                                  toast.success("Copied!");
                                }}
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        {/* Production Key */}
                        <div className="bg-white/5 rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-viraly-light flex items-center gap-2">
                                Production API Key
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  selectedEnterpriseDetail.production_api_key_active ? 'bg-green-500/20 text-green-400' :
                                  selectedEnterpriseDetail.production_requested ? 'bg-yellow-500/20 text-yellow-400' :
                                  'bg-gray-500/20 text-gray-400'
                                }`}>
                                  {selectedEnterpriseDetail.production_api_key_active ? 'Active' :
                                   selectedEnterpriseDetail.production_requested ? 'Pending Approval' : 'Not Requested'}
                                </span>
                              </p>
                              <p className="text-xs text-viraly-muted mt-1">
                                {selectedEnterpriseDetail.production_api_key_active 
                                  ? 'Production key is managed by the enterprise directly'
                                  : 'Request production access once sandbox testing is complete'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                      {selectedEnterpriseDetail.onboarding_status === 'sandbox_testing' && 
                       !selectedEnterpriseDetail.production_requested && 
                       !selectedEnterpriseDetail.production_api_key_active && (
                        <Button
                          onClick={() => handleRequestProduction(selectedEnterpriseDetail.id)}
                          disabled={productionRequestLoading}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid="request-production-btn"
                        >
                          {productionRequestLoading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Requesting...
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Request Production Access
                            </>
                          )}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        onClick={() => setSelectedEnterpriseDetail(null)}
                      >
                        Close
                      </Button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
        
        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <DocumentsSection
            entityType="partner"
            entityId={partner?.id}
            entityName={partner?.contact_name}
            entityEmail={partner?.email}
            entityTitle="Partner"
            accentColor="orange"
          />
        )}
        
        {/* Performance Tab */}
        {activeTab === 'performance' && (
          <div className="space-y-6">
            {/* Current Tier Status */}
            <div className="card-base p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-viraly-light flex items-center gap-2">
                  <Award className="w-5 h-5 text-purple-400" />
                  Your Tier Status
                </h3>
                {tierStatus && (
                  <span className="text-xs bg-purple-500/20 text-purple-300 px-3 py-1 rounded-full">
                    Year {(tierStatus.years_as_partner || 0) + 1}
                  </span>
                )}
              </div>
              
              {tierStatus ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white/5 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-purple-400">{tierStatus.tier_name}</p>
                    <p className="text-xs text-viraly-muted mt-1">Current Tier</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-green-400">{(tierStatus.commission_rate * 100).toFixed(1)}%</p>
                    <p className="text-xs text-viraly-muted mt-1">Commission Rate</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-blue-400">{tierStatus.active_enterprises}</p>
                    <p className="text-xs text-viraly-muted mt-1">Active Enterprises</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-yellow-400">${tierStatus.revenue_current_period_cad?.toLocaleString()}</p>
                    <p className="text-xs text-viraly-muted mt-1">Revenue This Period</p>
                  </div>
                </div>
              ) : (
                <p className="text-viraly-muted">Loading tier status...</p>
              )}
              
              {/* Next Tier Progress */}
              {tierStatus?.next_tier && (
                <div className="mt-6 p-4 bg-purple-500/10 rounded-lg border border-purple-500/20">
                  <p className="text-sm text-viraly-light mb-3">
                    <span className="font-bold">Next Level:</span> {tierStatus.next_tier.name} ({(tierStatus.next_tier.commission_rate * 100).toFixed(1)}% commission)
                  </p>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-viraly-muted">Enterprises needed:</p>
                      <p className="text-purple-300 font-semibold">{tierStatus.next_tier.enterprises_needed} more</p>
                    </div>
                    <div>
                      <p className="text-viraly-muted">Revenue needed:</p>
                      <p className="text-purple-300 font-semibold">${tierStatus.next_tier.revenue_needed_cad?.toLocaleString()} CAD more</p>
                    </div>
                  </div>
                </div>
              )}
              
              {tierStatus && !tierStatus.next_tier && (
                <div className="mt-6 p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/20 text-center">
                  <Award className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                  <p className="text-yellow-300 font-bold">Congratulations! You're at the highest tier!</p>
                </div>
              )}
            </div>
            
            {/* Tier Levels Reference */}
            <div className="card-base p-6">
              <h3 className="text-lg font-bold text-viraly-light mb-4">All Tier Levels</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-3 px-4 text-viraly-muted">Level</th>
                      <th className="text-left py-3 px-4 text-viraly-muted">Commission</th>
                      <th className="text-left py-3 px-4 text-viraly-muted">Min Enterprises</th>
                      <th className="text-left py-3 px-4 text-viraly-muted">Min Revenue (12mo)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tierStatus?.all_tiers && Object.entries(tierStatus.all_tiers)
                      .sort((a, b) => (a[1].level || 0) - (b[1].level || 0))
                      .map(([key, tier]) => (
                        <tr key={key} className={`border-b border-white/5 ${tierStatus.tier === key ? 'bg-purple-500/10' : ''}`}>
                          <td className="py-3 px-4">
                            <span className={tierStatus.tier === key ? 'text-purple-400 font-bold' : 'text-viraly-light'}>
                              {tier.name} {tierStatus.tier === key && '(Current)'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-green-400">{(tier.commission_rate * 100).toFixed(1)}%</td>
                          <td className="py-3 px-4 text-viraly-muted">{tier.min_active_enterprises}</td>
                          <td className="py-3 px-4 text-viraly-muted">${tier.min_revenue_12m_cad?.toLocaleString()} CAD</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-viraly-muted mt-4">
                * Tier evaluation happens at each anniversary of your partner onboarding date.
              </p>
            </div>
            
            {/* Payout History */}
            <div className="card-base p-6">
              <h3 className="text-lg font-bold text-viraly-light mb-4 flex items-center gap-2">
                <Banknote className="w-5 h-5 text-green-400" />
                Payout History
              </h3>
              
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-white/5 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-yellow-400">
                    ${(payoutHistory?.pending_balance_cad || 0).toFixed(2)}
                  </p>
                  <p className="text-xs text-viraly-muted">Pending Balance</p>
                </div>
                <div className="bg-white/5 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-400">
                    ${(payoutHistory?.total_paid_out_cad || 0).toFixed(2)}
                  </p>
                  <p className="text-xs text-viraly-muted">Total Paid Out</p>
                </div>
                <div className="bg-white/5 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-viraly-light">
                    ${payoutHistory?.payout_threshold_cad || 100}
                  </p>
                  <p className="text-xs text-viraly-muted">Min Threshold</p>
                </div>
              </div>
              
              {payoutHistory?.can_request_payout && (
                <div className="mb-6">
                  <Button 
                    className="w-full bg-green-500 hover:bg-green-600"
                    onClick={() => setRequestingPayout(true)}
                  >
                    <Banknote className="w-4 h-4 mr-2" />
                    Request Payout
                  </Button>
                </div>
              )}
              
              {payoutHistory?.payout_message && !payoutHistory?.can_request_payout && (
                <div className="mb-6 p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                  <p className="text-sm text-yellow-300">{payoutHistory.payout_message}</p>
                </div>
              )}
              
              {/* Monthly Stats */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-white/5 rounded-lg p-4">
                  <p className="text-xs text-viraly-muted mb-1">This Month ({payoutHistory?.current_month})</p>
                  <p className="text-xl font-bold text-blue-400">${(payoutHistory?.monthly_commission_cad || 0).toFixed(2)}</p>
                </div>
                <div className="bg-white/5 rounded-lg p-4">
                  <p className="text-xs text-viraly-muted mb-1">All-Time Earnings</p>
                  <p className="text-xl font-bold text-purple-400">${(payoutHistory?.total_earned_cad || 0).toFixed(2)}</p>
                </div>
              </div>
              
              {payoutHistory?.payouts?.length > 0 ? (
                <div className="space-y-3">
                  {payoutHistory.payouts.map((p) => (
                    <div key={p.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div>
                        <p className="text-viraly-light font-medium">${p.amount_cad?.toFixed(2)} CAD</p>
                        <p className="text-xs text-viraly-muted">{new Date(p.created_at).toLocaleDateString()}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${
                        p.status === 'paid' ? 'bg-green-500/20 text-green-400' :
                        p.status === 'approved' ? 'bg-blue-500/20 text-blue-400' :
                        p.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {p.status}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-viraly-muted text-center py-4">No payout history yet</p>
              )}
            </div>
          </div>
        )}
        
        {/* Resources Tab */}
        {activeTab === 'resources' && (
          <div className="space-y-6">
            <div className="card-base p-6">
              <h3 className="text-lg font-bold text-viraly-light mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-400" />
                Partner Marketing Kit
              </h3>
              <p className="text-viraly-muted mb-4">
                Download your official partner resources including email templates, pricing overview, and customizable one-pager.
              </p>
              <a 
                href="/downloads/Viraly_Partner_Marketing_Kit.docx"
                download
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
              >
                <Download className="w-4 h-4" />
                Download Marketing Kit (.docx)
              </a>
            </div>
            
            <div className="card-base p-6">
              <h3 className="text-lg font-bold text-viraly-light mb-4">Quick Resources</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="font-bold text-viraly-light mb-2">Your Referral Link</h4>
                  <p className="text-sm text-viraly-muted mb-2">Share this link with potential enterprise clients:</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs bg-black/30 p-2 rounded overflow-x-auto">
                      {`https://viraly.ca/enterprise?ref=${partner?.referral_code || 'YOUR_CODE'}`}
                    </code>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(`https://viraly.ca/enterprise?ref=${partner?.referral_code}`);
                        setCopiedLink(true);
                        setTimeout(() => setCopiedLink(false), 2000);
                      }}
                    >
                      {copiedLink ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                
                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="font-bold text-viraly-light mb-2">Partner Support</h4>
                  <p className="text-sm text-viraly-muted mb-2">Questions about the partner program?</p>
                  <a href="mailto:care@viraly.ca" className="text-blue-400 hover:underline text-sm">
                    care@viraly.ca
                  </a>
                </div>
              </div>
            </div>
            
            <div className="card-base p-6">
              <h3 className="text-lg font-bold text-viraly-light mb-4">What's in the Marketing Kit</h3>
              <ul className="space-y-2 text-viraly-muted">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-400 mt-0.5" />
                  <span><strong>Email Templates</strong> - Cold outreach, follow-up, and referral emails</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-400 mt-0.5" />
                  <span><strong>Pricing Overview</strong> - Enterprise plans with pricing and features</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-400 mt-0.5" />
                  <span><strong>One-Pager Template</strong> - Customizable with your company details</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-400 mt-0.5" />
                  <span><strong>Commission Program</strong> - Full tier breakdown and payout info</span>
                </li>
              </ul>
            </div>
          </div>
        )}
      </main>
      
      {/* How to Use Guide Modal */}
      <Dialog open={showGuide} onOpenChange={setShowGuide}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto bg-viraly-card border-viraly-border">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading flex items-center gap-3">
              <BookOpen className="w-7 h-7 text-blue-400" />
              <span className="text-blue-400">Partner Guide</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {partnerGuideSteps.map((step, index) => (
              <div 
                key={index}
                className="bg-viraly-dark/50 rounded-xl p-5 border border-white/5 hover:border-blue-500/30 transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-blue-500/10 text-blue-400">
                    <step.icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
                    <p className="text-viraly-muted mb-3">{step.description}</p>
                    <div className="space-y-1">
                      {step.tips.map((tip, tipIdx) => (
                        <div key={tipIdx} className="flex items-center gap-2 text-sm text-viraly-muted/80">
                          <Zap className="w-3 h-3 text-blue-400" />
                          <span>{tip}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Download Full Guide */}
            <div className="bg-blue-500/10 rounded-xl p-5 border border-blue-500/20">
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-400" />
                Download Full Guide
              </h3>
              <p className="text-viraly-muted text-sm mb-4">
                Get the complete partner onboarding guide with detailed instructions, screenshots, and FAQ.
              </p>
              <a 
                href="/downloads/Viraly_Partner_Onboarding_Guide.docx"
                download
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                <Download className="w-4 h-4" />
                Download Guide (.docx)
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Payout Request Modal */}
      <Dialog open={requestingPayout} onOpenChange={setRequestingPayout}>
        <DialogContent className="bg-viraly-card border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="w-5 h-5 text-green-400" />
              Request Payout
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/20">
              <p className="text-sm text-viraly-muted">Available for payout:</p>
              <p className="text-2xl font-bold text-green-400">
                ${(payoutHistory?.pending_balance_cad || 0).toFixed(2)} CAD
              </p>
              <div className="mt-2 text-xs text-viraly-muted space-y-1">
                <p>Minimum threshold: ${payoutHistory?.payout_threshold_cad || 500} CAD</p>
                <p>This month's earnings: ${(payoutHistory?.monthly_commission_cad || 0).toFixed(2)} CAD</p>
              </div>
            </div>
            
            <div>
              <label className="text-sm text-viraly-muted block mb-1">PayPal Email *</label>
              <input
                type="email"
                value={paypalEmail}
                onChange={(e) => setPaypalEmail(e.target.value)}
                placeholder="your-paypal@email.com"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-viraly-light"
              />
              <p className="text-xs text-viraly-muted mt-1">
                We'll send your payment to this PayPal account
              </p>
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button 
                className="flex-1 bg-green-500 hover:bg-green-600"
                disabled={!paypalEmail || (payoutHistory?.pending_balance_cad || 0) < (payoutHistory?.payout_threshold_cad || 100)}
                onClick={async () => {
                  const partnerId = localStorage.getItem("partnerId");
                  const token = localStorage.getItem("partnerToken");
                  try {
                    await apiClient.post(`/b2b/partners/${partnerId}/request-payout`, {
                      payout_method: "paypal",
                      paypal_email: paypalEmail
                    }, { headers: { Authorization: `Bearer ${token}` } });
                    toast.success("Payout request submitted! We'll process it within 2-3 business days.");
                    setRequestingPayout(false);
                    setPaypalEmail("");
                    // Refresh data
                    fetchPartnerData(partnerId, token);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Failed to request payout");
                  }
                }}
              >
                Submit Request
              </Button>
              <Button variant="outline" onClick={() => setRequestingPayout(false)}>
                Cancel
              </Button>
            </div>
            
            <p className="text-xs text-viraly-muted text-center">
              Payouts are processed manually within 2-3 business days. You'll receive an email notification when complete.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
